/**
 * SapmForm — loads a SAP Commerce iframe form, tracks submission, and monitors URL changes.
 *
 * Replaces T.Module.SapmForm (c-sapm-form.js). The `setTitleForIframe` has been inlined as vanilla DOM.
 */
import { handleCustomUDL } from '@utils/UDLUtils';

/** CSS selectors for elements inside the iframe's document. */
const SELECTORS = {
  /** Lead category dropdown within the SAP Commerce form. */
  select: '.sapCpCInteractionAttribute-YY1_LEADCATEGORY_MIA select',
} as const;

export class SapmForm {
  /** Reference to the iframe element managed by this instance. */
  private ctx: HTMLIFrameElement;
  /** MutationObserver watching for submission completion in the iframe's document. */
  private observer: MutationObserver | null = null;
  /** Last dispatched URL, used to avoid duplicate change events. */
  private lastDispatched: string | null = null;
  /** Handler attached to the iframe's unload event. */
  private unloadHandler: (() => void) | null = null;
  /** Flag indicating whether a form submission has been tracked via UDL. */
  private formSubmitTracked: boolean = false;
  /** Timeout in milliseconds before redirect, used for waiting on UDL events to fire (default: 800ms). */
  private formSubmitTimeoutBeforeRedirect: number = 800;

  /**
   * Creates a new SapmForm instance.
   * @param el - The iframe element to manage.
   */
  constructor(el: HTMLIFrameElement) {
    this.ctx = el;
  }

  /** Initializes the form: sets src, attaches event listeners, and starts monitoring. Returns `this` for chaining. */
  init(): this {
    const src = this.ctx.getAttribute('data-src');
    if (src) {
      this.ctx.setAttribute('src', src + window.location.search);
    }

    this.unloadHandler = () => setTimeout(() => this.dispatchChange(), 0);
    this.attachUnload();

    this.ctx.addEventListener('load', () => {
      this.setTitle();

      const form = this.ctx.contentDocument?.querySelector('form') ?? null;
      if (form) {
        this.setupMutationObserver(form);
      }

      this.attachUnload();
      this.enableScrollingOnMobile();
      this.dispatchChange();
    });

    return this;
  }

  // ===========================================================================
  // Private helpers
  // ===========================================================================

  /** Sets the iframe's `title` attribute from its document title for accessibility (if not already set). */
  private setTitle(): void {
    const doc = this.ctx.contentDocument;
    if (!doc) return;
    const title = doc.querySelector('head title')?.textContent?.trim();
    if (title && !this.ctx.getAttribute('title')) {
      this.ctx.setAttribute('title', title);
    }
  }

  private setupMutationObserver(form: HTMLFormElement): void {
    const config: MutationObserverInit = { attributes: true, childList: false, subtree: false };
    this.observer = new MutationObserver((mutationList, observer) => {
      for (const mutation of mutationList) {
        if (
          mutation.attributeName === 'class' &&
          (mutation.target as Element).classList.contains('sapCpSubmitCompleted')
        ) {
          this.trackFormSubmit();
          observer.disconnect();
        }
      }
    });
    this.observer.observe(form, config);
  }

  /** Sends a UDL event for the form submission with common parameters. */
  private trackFormSubmit(): void {

    try {
      handleCustomUDL('form_submit', this.getCommonParameters(), () => { this.formSubmitTracked = true}, this.formSubmitTimeoutBeforeRedirect);
    } catch (error) {
      console.error(error);
    }
  }

  /** Builds the common parameters object for UDL tracking (form name, page URL, type, and lead category). */
  private getCommonParameters(): Record<string, string> {
    const formAddress = this.ctx.getAttribute('data-src') ?? '';
    // Extract path segment after `sapmform.html/` in the data-src URL.
    const regex = /.*sapmform\.html\//;
    const doc = this.ctx.contentDocument;
    const select = doc?.querySelector<HTMLSelectElement>(SELECTORS.select);
    const selectValue = select?.value ?? '';
    const leadCategory =
      doc?.querySelector<HTMLOptionElement>(
        `${SELECTORS.select} option[value="${selectValue}"]`,
      )?.label ?? '';

    return {
      form_name: formAddress.replace(regex, ''),
      page_url: window.location.href,
      form_type: 'SAPMC',
      trigger: leadCategory,
    };
  }

  /** Dispatches a change event based on the iframe's current URL, avoiding duplicates. */
  private dispatchChange(): void {
    try {
      const newHref = this.ctx.contentWindow?.location.href;
      if (newHref && newHref !== this.lastDispatched) {
        this.handleURLChange(newHref);
        this.lastDispatched = newHref;
      }
    } catch {
      // Cross-origin access — silently ignore.
    }
  }


  /** Waits until `checkValue` returns true or the timeout expires, using requestAnimationFrame for polling. */
  private async waitUntilTrue(checkValue: () => boolean, timeout = this.formSubmitTimeoutBeforeRedirect): Promise<void> {
    return new Promise((resolve) => {
      const started = performance.now();
      const check = () => {
        if (checkValue() || performance.now() - started >= timeout) {
          resolve();
          return;
        }
        requestAnimationFrame(check);
      };
      check();
    });
  }

  /** Handles URL changes from the iframe: tracks submission, waits for UDL event, then redirects. */
  private  handleURLChange(newURL: string): void {
    if (newURL && !newURL.includes('/bin/straumann/sapmform.html')) {
      this.trackFormSubmit();
      // We need to wait before unloading the page for the event to be sent to gtm.
      // max time out is configurable but defaults to 800ms
      this.waitUntilTrue(() => this.formSubmitTracked).then(() => {
          window.top!.location.href = newURL;
      })
    }
  }

  /** Attaches the unload handler to the iframe's content window (with cross-origin safety). */
  private attachUnload(): void {
    try {
      const cw = this.ctx.contentWindow;
      if (cw && this.unloadHandler) {
        cw.removeEventListener('unload', this.unloadHandler);
        cw.addEventListener('unload', this.unloadHandler);
      }
    } catch {
      // Cross-origin access — silently ignore.
    }
  }

  /** Enables scrolling on the iframe when viewport width is below 600px (mobile). */
  private enableScrollingOnMobile(): void {
    if (document.documentElement.clientWidth < 600) {
      this.ctx.setAttribute('scrolling', 'yes');
    }
  }

  /** Cleans up observers and event listeners. Called when the component is destroyed. */
  destroy(): void {
    this.observer?.disconnect();
    try {
      const cw = this.ctx.contentWindow;
      if (cw && this.unloadHandler) {
        cw.removeEventListener('unload', this.unloadHandler);
      }
    } catch {
      // Cross-origin access — silently ignore.
    }
  }

  /**
   * Initializes all SapmForm instances found within the given root element.
   * @param root - The container to search for `[data-component="SapmForm"]` elements. Defaults to `document`.
   */
  static initAll(root: Document | HTMLElement = document): void {
    root
      .querySelectorAll<HTMLIFrameElement>('[data-component="sapm-form"]')
      .forEach((el) => new SapmForm(el).init());
  }
}
